import {
    addLegendToCanvas,
    getSymbolStyle,
    fromLonLatArr,
} from '@/js/util/map_functionality.js';

import { MapPage } from '@/js/classes/map_page/map_page.js';
import { InfoModal } from '@/js/classes/modal.js';
import { isBlankValue, unixTimestampToReadableDate, hexToRgba } from '@/js/util.js';
import { assertId } from '@/js/util/asserts.js';
import {FeatureType, MapControl, MapSymbol} from '@/js/util/shared_constants.js';

import { Feature } from 'ol';
import { Point, LineString, Polygon } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { boundingExtent } from 'ol/extent';
import { Fill, Stroke, Style, Text } from 'ol/style';


export class MapPageExternal extends MapPage {
    /* extends parent */ constructor(shared_map_data) {
        super();
        this.shared_map_data = shared_map_data;
        this.asset_lookup = {};
        this.map_settings['map_imagery'] = shared_map_data['map_imagery'];
        this.map_settings['show_polygon_edges'] = shared_map_data['show_polygon_edges'] ?? 'Yes';

        this.using_controls.push(...[
            MapControl.LocateMe,
            MapControl.CenterOnFeaturesExternal,
            // MapControl.GenerateMap, // TODO
            (('legend' in this.shared_map_data) ? MapControl.ToggleLegend : null),
        ]);

        this.control_options[MapControl.ToggleLegend] = { 'start_shown': ('legend' in this.shared_map_data) };
    }

    /* extends parent */ async setInitialMapCenter() {
        if(this.shared_map_data['project_info'] && this.shared_map_data['project_info']['centerpoint']) {
            this.view.setCenter(fromLonLat(this.shared_map_data['project_info']['centerpoint']));
        } else {
            await super.setInitialMapCenter();
        }
    }

    /* extends parent */ async main() {
        await super.main();

        for(const asset of this.shared_map_data['assets']) {
            this.asset_lookup[asset.asset_id] = asset;
            this.addAssetToMap(asset);
        }

        this.map.getLayers().forEach((layer) => {
            const type = layer.get('type');
            if(!type) { return; }
            if(type !== 'base') { return; }
            const title = layer.get('title');
            if(!title) { return; }
            if(title === this.shared_map_data['map_imagery']) {
                layer.setVisible(true);
                layer.setOpacity((this.shared_map_data['map_imagery_opacity'] ?? 100) / 100);
            } else {
                layer.setVisible(false);
            }
        });

        this.map_elem.style.backgroundColor = this.shared_map_data['map_imagery_background'];

        /* Add custom control */ {
            /* institute logo */ {
                const logo_elem = document.createElement('img');
                logo_elem.src = 'static/davey_institute_logo_small.png';
                logo_elem.id = 'institute-logo-overlay';
                this.map.getViewport().appendChild(logo_elem);
            }

            /* map title */ {
                const map_name = this.shared_map_data['map_name'];
                const date_generated = unixTimestampToReadableDate(this.shared_map_data['timestamp_generated'], { 'dateOnly': true });

                const title_container_elem = document.createElement('div');
                title_container_elem.id = 'map-title-overlay-container';

                const title_elem = document.createElement('span');
                title_elem.id = 'map-title-overlay';
                title_elem.textContent = `${map_name} (${date_generated})`;
                title_container_elem.appendChild(title_elem);

                this.map.getViewport().appendChild(title_container_elem);
            }
        }

        this.renderOverlayLegend();
    }

    getStyleForFeature(feature, resolution) {
        const asset_id = feature.get('asset_id');
        assertId(asset_id);

        if(this.individual_marker_cache[asset_id]) { return this.individual_marker_cache[asset_id]; }

        const asset = this.asset_lookup[asset_id];
        const map_style = asset['map_style'];
        const feature_type = map_style['feature_type'] || FeatureType.Point;
        const style_options = {};

        const visual_style_property_name = (feature_type === FeatureType.Point ? 'image' : 'stroke');

        style_options[visual_style_property_name] = getSymbolStyle(
            map_style['marker_symbol'] ?? (feature_type === FeatureType.Point ? MapSymbol.Circle : MapSymbol.SolidLine),
            map_style['marker_radius'],
            map_style['marker_fill_color'],
            map_style['marker_stroke_color'],
            map_style['marker_stroke_width'],
            true,
        );

        if(feature_type === FeatureType.Polygon) {
            style_options.fill = new Fill({
                color: hexToRgba(map_style['marker_fill_color'], 0.5),
            });

            if(this.map_settings['show_polygon_edges'] === 'No') { delete style_options['stroke']; }
        }

        if('text_content' in map_style) {
            style_options['text'] = new Text({
                text: map_style['text_content'],
                font: map_style['text_font'],
                placement: 'point',
                offsetX: map_style['text_offset_x'],
                offsetY: map_style['text_offset_y'],
                overflow: true,
                textAlign: map_style['text_align'],
                textBaseline: map_style['text_baseline'],
                fill: new Fill({
                    color: map_style['text_fill_color'],
                }),
                stroke: new Stroke({
                    color: map_style['text_stroke_color'],
                    width: map_style['text_stroke_width'],
                }),
            });
        }

        const style = new Style(style_options);
        this.individual_marker_cache[asset_id] = style;
        return style;
    }

    async onMapClick(event) {
        let feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => feature);

        if(!feature) { return; }

        const features = feature.get('features');
        if(features) {
            if(features.length === 0) {
                console.error('Clicked on feature had 0 inner features (unexpected).');
                return;

            } else if(features.length === 1) {
                feature = features[0];

            } else {
                const extent = boundingExtent(features.map((r) => r.getGeometry().getCoordinates()));
                this.map.getView().fit(extent, {
                    duration: 250,
                    padding: [0, 0, 0, 0],
                    constrainResolution: false,
                    maxZoom: 22,
                });
                return;
            }
        }

        const asset_id = feature.get('asset_id');
        if(asset_id === 'tracking_marker') { return; }

        const asset = this.asset_lookup[asset_id];
        const asset_modal = new InfoModal('', 'View Asset');

        /* Generate table elements */ {
            const table = document.createElement('table');
            table.className = 'asset-data-popup w-100';

            const thead = document.createElement('thead');
            table.appendChild(thead);

            const headerRow = document.createElement('tr');
            thead.appendChild(headerRow);

            const thField = document.createElement('th');
            thField.textContent = 'Field';
            headerRow.appendChild(thField);

            const thValue = document.createElement('th');
            thValue.textContent = 'Value';
            headerRow.appendChild(thValue);

            const tbody = document.createElement('tbody');

            const asset_field_order = this.shared_map_data['asset_field_order'];
            for(const field of asset_field_order[asset['asset_type']]) {
                const value = asset['asset_data'][field];
                if(isBlankValue(value)) { continue; }

                const tr = document.createElement('tr');

                const tdField = document.createElement('td');
                tdField.textContent = field;
                tr.appendChild(tdField);

                const tdValue = document.createElement('td');
                tdValue.textContent = value;
                tr.appendChild(tdValue);

                if('legend' in this.shared_map_data && field===this.shared_map_data['legend']['title']) {
                    const color_marker = document.createElement('div');
                    color_marker.classList.add('asset-data-popup-color-marker');
                    color_marker.style.backgroundColor = asset['map_style']['marker_fill_color'];
                    tdValue.prepend(color_marker);
                    tdValue.classList.add('asset-data-popup-cell-with-color-marker');
                }

                tbody.appendChild(tr);
            }

            table.appendChild(tbody);
            asset_modal.appendNode(table);
        }

        asset_modal.show();
    }

    addAssetToMap(asset) {
        const feature_type = asset['map_style'] ? asset['map_style']['feature_type'] : FeatureType.Point;
        let asset_marker;

        if(feature_type === FeatureType.Point) {
            const coords = fromLonLat([asset['coordinates'][0], asset['coordinates'][1]]);
            asset_marker = new Feature({ geometry: new Point(coords) });

        } else if(feature_type === FeatureType.Line) {
            const coords = fromLonLatArr(asset['coordinates']);
            asset_marker = new Feature({ geometry: new LineString(coords) });

        } else if(feature_type === FeatureType.Polygon) {
            const coords = fromLonLatArr(asset['coordinates']);
            asset_marker = new Feature({ geometry: new Polygon(coords) });

        }

        if(asset_marker) {
            asset_marker.set('asset_id', asset['asset_id']);
            this.marker_source.addFeature(asset_marker);
        }
    }

    /* extends parent */ renderOverlayLegend() {
        super.renderOverlayLegend();

        if('legend' in this.shared_map_data) {
            const [mapCanvasWidth, mapCanvasHeight] = this.map.getSize();

            addLegendToCanvas({
                'ctx': this.legend_canvas_elem.getContext('2d'),
                'map_height': mapCanvasHeight,
                'map_width': mapCanvasWidth,
                'legend_heading': this.shared_map_data['legend']['title'],
                'legend_items': this.shared_map_data['legend']['items'],
                'HEADING_FONT': 'bold 16px Arial',
                'LABEL_FONT': '16px Arial',
                'LEGEND_PADDING': 10,
                'CIRCLE_RADIUS': 10,
                'ROW_HEIGHT': 24,
                'CORNER': 'bottom-right',
                'OVERLAY_MARGIN_X': 10,
                'OVERLAY_MARGIN_Y': 40,
            });
        }
    }
}